import { store } from '../index';
import { setActiveFacilities, addActiveFacility } from '../store/reducers/activeFacilities.slice';
import { setSelectedAccount } from '../store/reducers/common.slice';

/**
 * Utility functions for managing facilityId and accountId in a multi-tab safe way
 * 
 * Key principle: Redux state (tab-specific) is the source of truth.
 * localStorage is only used for persistence on page refresh.
 */

/**
 * Sets facilityId and updates Redux state first (tab-specific), then localStorage (for persistence)
 * 
 * IMPORTANT: This ensures each tab maintains its own facilityId independently.
 * Changing facilityId in one tab won't affect other tabs because Redux is tab-specific.
 * 
 * @param {string|string[]} facilityIds - Single facility ID or array of facility IDs
 * @param {Object} options - Optional configuration
 * @param {boolean} options.updateLocalStorage - Whether to update localStorage (default: true)
 * @param {boolean} options.addToExisting - If true, add to existing facilities instead of replacing (default: false)
 */
export const setFacilityId = (facilityIds, options = {}) => {
  const {
    updateLocalStorage = true,
    addToExisting = false
  } = options;

  // Ensure facilityIds is an array
  const facilitiesArray = Array.isArray(facilityIds) ? facilityIds : [facilityIds].filter(Boolean);

  // Update Redux state FIRST (this is tab-specific and won't affect other tabs)
  if (addToExisting) {
    // Add to existing facilities (for initial load scenarios)
    const currentFacilities = store.getState().activeFacilities.facilities || [];
    const uniqueFacilities = [...new Set([...currentFacilities, ...facilitiesArray])];
    store.dispatch(setActiveFacilities(uniqueFacilities));
  } else {
    // Replace existing facilities
    store.dispatch(setActiveFacilities(facilitiesArray));
  }

  // Update localStorage AFTER Redux (for persistence on page refresh)
  // 
  // IMPORTANT: localStorage changes WILL trigger storage events in other tabs,
  // but those tabs will IGNORE the change because they read from Redux first.
  // Each tab maintains its own Redux state independently, so:
  // - Tab 1 sets Facility C → localStorage = "C"
  // - Tab 2 sees localStorage change but continues using Facility B from its Redux state
  // - Complete isolation: each tab uses its own facility, regardless of localStorage
  if (updateLocalStorage && facilitiesArray.length > 0) {
    try {
      sessionStorage.setItem("facilityId", facilitiesArray[0]);
    } catch (error) {
      console.warn('Failed to set facilityId in sessionStorage:', error);
    }
  } else if (updateLocalStorage && facilitiesArray.length === 0) {
    // Clear sessionStorage if no facilities
    try {
      sessionStorage.removeItem("facilityId");
    } catch (error) {
      console.warn('Failed to remove facilityId from sessionStorage:', error);
    }
  }
};

/**
 * Gets facilityId from Redux state (tab-specific), with localStorage fallback
 * 
 * @returns {string|null} - Facility ID or null
 */
export const getFacilityId = () => {
  try {
    // First try Redux state (tab-specific)
    const state = store.getState();
    const activeFacilities = state?.activeFacilities?.facilities;
    
    if (activeFacilities && activeFacilities.length > 0) {
      return activeFacilities[0];
    }

    // Fallback to sessionStorage (for initial load)
    return sessionStorage.getItem("facilityId");
  } catch (error) {
    console.warn('Error getting facilityId:', error);
    // Final fallback
    try {
      return sessionStorage.getItem("facilityId");
    } catch (e) {
      return null;
    }
  }
};

/**
 * Adds a facility to the active facilities list
 * 
 * @param {string} facilityId - Facility ID to add
 */
export const addFacilityId = (facilityId) => {
  if (!facilityId) return;
  
  const currentFacilities = store.getState().activeFacilities.facilities || [];
  
  if (!currentFacilities.includes(facilityId)) {
    store.dispatch(addActiveFacility(facilityId));
    
    // Update sessionStorage if this is the first facility
    if (currentFacilities.length === 0) {
      try {
        sessionStorage.setItem("facilityId", facilityId);
      } catch (error) {
        console.warn('Failed to set facilityId in sessionStorage:', error);
      }
    }
  }
};

/**
 * Removes a facility from the active facilities list
 * 
 * @param {string} facilityId - Facility ID to remove
 */
export const removeFacilityId = (facilityId) => {
  const { setActiveFacilities } = require('../store/reducers/activeFacilities.slice');
  const currentFacilities = store.getState().activeFacilities.facilities || [];
  
  if (currentFacilities.includes(facilityId)) {
    if (currentFacilities.length === 1) {
      // If removing the last facility, clear everything
      store.dispatch(setActiveFacilities([]));
      try {
        sessionStorage.removeItem("facilityId");
      } catch (error) {
        console.warn('Failed to remove facilityId from sessionStorage:', error);
      }
    } else {
      // Remove from array
      const updatedFacilities = currentFacilities.filter(id => id !== facilityId);
      store.dispatch(setActiveFacilities(updatedFacilities));
      
      // Update sessionStorage with the first remaining facility
      if (updatedFacilities.length > 0) {
        try {
          sessionStorage.setItem("facilityId", updatedFacilities[0]);
        } catch (error) {
          console.warn('Failed to update facilityId in sessionStorage:', error);
        }
      }
    }
  }
};

/**
 * Sets accountId and updates Redux state first (tab-specific), then localStorage (for persistence)
 * 
 * IMPORTANT: This ensures each tab maintains its own accountId independently.
 * Changing accountId in one tab won't affect other tabs because Redux is tab-specific.
 * 
 * @param {string|Object} accountIdOrAccount - Account ID string or full account object
 * @param {Object} options - Optional configuration
 * @param {boolean} options.updateLocalStorage - Whether to update localStorage (default: true)
 */
export const setAccountId = (accountIdOrAccount, options = {}) => {
  const {
    updateLocalStorage = true
  } = options;

  // Handle both account ID string and account object
  const accountId = typeof accountIdOrAccount === 'string' 
    ? accountIdOrAccount 
    : accountIdOrAccount?._id;
  
  const accountObject = typeof accountIdOrAccount === 'object' && accountIdOrAccount?._id
    ? accountIdOrAccount
    : null;

  if (!accountId) {
    console.warn('setAccountId: No accountId provided');
    return;
  }

  // Update Redux state FIRST (this is tab-specific and won't affect other tabs)
  if (accountObject) {
    store.dispatch(setSelectedAccount(accountObject));
  } else {
    // If only ID provided, try to find account in current state
    const state = store.getState();
    const accounts = state?.account || [];
    const account = accounts.find(acc => acc._id === accountId);
    if (account) {
      store.dispatch(setSelectedAccount(account));
    } else {
      // If account not found, create minimal object
      store.dispatch(setSelectedAccount({ _id: accountId }));
    }
  }

  // Update sessionStorage AFTER Redux (for persistence on page refresh)
  // 
  // IMPORTANT: sessionStorage changes WILL NOT trigger storage events in other tabs,
  // and each tab maintains its own sessionStorage independently, so:
  // - Tab 1 sets Account C → sessionStorage = "C"
  // - Tab 2 continues using Account B from its Redux state/sessionStorage
  // - Complete isolation: each tab uses its own account, regardless of other tabs
  if (updateLocalStorage) {
    try {
      sessionStorage.setItem("accountId", accountId);
    } catch (error) {
      console.warn('Failed to set accountId in sessionStorage:', error);
    }
  }
};

/**
 * Gets accountId from Redux state (tab-specific), with localStorage fallback
 * 
 * @returns {string|null} - Account ID or null
 */
export const getAccountId = () => {
  try {
    // First try Redux state (tab-specific)
    const state = store.getState();
    const selectedAccount = state?.common?.selectedAccount;
    
    if (selectedAccount && selectedAccount._id) {
      return selectedAccount._id;
    }

    // Fallback to sessionStorage (for initial load)
    return sessionStorage.getItem("accountId");
  } catch (error) {
    console.warn('Error getting accountId:', error);
    // Final fallback
    try {
      return sessionStorage.getItem("accountId");
    } catch (e) {
      return null;
    }
  }
};
